/**
 * Centralized query-key factory for TanStack Query.
 * No inline key arrays — all keys go through qk.<resource>(...).
 *
 * Tenant context is implicit from the auth token — tenant_id is NEVER
 * included in a query key from client input (coding-standards §3).
 *
 * [Source: architecture/frontend-architecture.md#3-server-state-tanstack-query-v5]
 * [Source: architecture/coding-standards.md#3-react-server-state-vs-ui-state]
 */

export const qk = {
  /**
   * Inbox task list (GET /tasks).
   * Filters are the query params object passed to the list endpoint.
   */
  inbox: (filters: Record<string, unknown> = {}) =>
    ['tasks', 'list', filters] as const,

  /**
   * Single task split-view (GET /tasks/:id).
   */
  task: (id: string) => ['tasks', 'detail', id] as const,

  /**
   * Posteingang list (GET /posteingang) and its open-count (epic B2).
   * Keyed separately from `inbox` because it is a different resource: the Eingangsliste
   * pages COMMUNICATIONS across four channels, not the task worklist.
   */
  posteingang: (filters: Record<string, unknown> = {}) =>
    ['posteingang', 'list', filters] as const,
  // `mode` is part of the key (the qk.inbox precedent): live and mock rows are
  // different data, and a mode flip must not serve the other one from cache.
  posteingangCounts: (mode: 'live' | 'mock' = 'mock') =>
    ['posteingang', 'counts', mode] as const,
  /** P2 hardening 04: the caller's private-sender rows (GET /posteingang/sender-private). */
  senderPrivate: () => ['posteingang', 'sender-private'] as const,
  /** The Aktivitätenhistorie of one Eingang (GET /posteingang/:commId/activity). */
  posteingangActivity: (commId: string, mode: 'live' | 'mock' = 'mock') =>
    ['posteingang', 'activity', commId, mode] as const,
  /** One reply sent from an Eingang (GET /posteingang/:commId/sent/:sentId). */
  posteingangSentMail: (commId: string, sentId: string, mode: 'live' | 'mock' = 'mock') =>
    ['posteingang', 'sent', commId, sentId, mode] as const,
  /** The Policenkontrolle-Befund(e) of an Eingang (GET /posteingang/:commId/policenkontrolle) - P2 hardening 05. */
  posteingangPolicenkontrolle: (commId: string, mode: 'live' | 'mock' = 'mock') =>
    ['posteingang', 'policenkontrolle', commId, mode] as const,
  /** The stored status of one Eingang (GET /posteingang/:commId/status, migration 0141). */
  posteingangStatus: (commId: string, mode: 'live' | 'mock' = 'mock') =>
    ['posteingang', 'status', commId, mode] as const,
  /** Audit rows for ONE entity of an Eingang (Teil 5). Keyed per entity: the panel
   *  merges the communication's rows with each task's. */
  eingangAudit: (entityType: string, entityId: string, mode: 'live' | 'mock') =>
    ['posteingang', 'audit', entityType, entityId, mode] as const,
  /** One Eingang read on its own (no task) — the Bearbeitungsmaske's Teil-1 fallback. */
  eingangComm: (commId: string, mode: 'live' | 'mock') =>
    ['posteingang', 'comm', commId, mode] as const,

  /** The Eingang's stored files (Teil 1) and one task's Versandanhaenge (Teil 4). */
  eingangAttachments: (commId: string, mode: 'live' | 'mock') =>
    ['posteingang', 'attachments', commId, mode] as const,
  taskAttachments: (taskId: string, mode: 'live' | 'mock') =>
    ['tasks', taskId, 'attachments', mode] as const,

  /** The per-Aufgabe Antwortentwurf (GET/PUT /tasks/:id/draft). */
  taskDraft: (taskId: string) => ['tasks', taskId, 'draft'] as const,
  /** The checklist of an Übergabe task (GET /tasks/:id/uebergabe). */
  taskUebergabe: (taskId: string) => ['tasks', taskId, 'uebergabe'] as const,

  /**
   * Agent conversation replay (GET /agent/conversation).
   * Single persisted thread — no params needed (scoped by auth token).
   */
  agentConversation: () => ['agent', 'conversation'] as const,

  /**
   * Heute-Briefing Entscheid counts (GET /agent/briefing → PendingWorkSummary)
   * — Story UI-U10. Feature-detected; scoped by the auth token.
   */
  agentBriefing: () => ['agent', 'briefing'] as const,

  /**
   * Kunden-grid list (GET /customers) — Story V10-J.
   */
  customers: (filters: Record<string, unknown> = {}) =>
    ['customers', 'list', filters] as const,

  /**
   * Kunde-Ebene Risikoprofil (GET /customers/:t/:id/risk-profile) — P2 Placement Slice 1.
   * `mode` is part of the key so a live/mock flip never serves the other from cache.
   */
  riskProfile: (subjectType: string, subjectId: string, mode: 'live' | 'mock' = 'mock') =>
    ['customers', 'risk-profile', subjectType, subjectId, mode] as const,

  /**
   * Kunde-Ebene Basissparten-Checkliste (GET /customers/:t/:id/coverage-baseline) — P2 Slice 1.
   */
  coverageBaseline: (subjectType: string, subjectId: string, mode: 'live' | 'mock' = 'mock') =>
    ['customers', 'coverage-baseline', subjectType, subjectId, mode] as const,

  /**
   * Kunde-Ebene Schadenverlauf je Sparte (GET /customers/:t/:id/claims-history) — P2 hardening Q15.
   */
  customerClaimsHistory: (subjectType: string, subjectId: string, mode: 'live' | 'mock' = 'mock') =>
    ['customers', 'claims-history', subjectType, subjectId, mode] as const,

  /**
   * Renewals board (GET /renewals/board) — Story V10-J.
   */
  renewalsBoard: (bucket?: string) => ['renewals', 'board', bucket ?? 'all'] as const,

  /**
   * Heute Fälligkeiten aggregation (GET /renewals/heute) — Story V10-J.
   */
  renewalsHeute: () => ['renewals', 'heute'] as const,

  /**
   * Versicherer list (GET /carriers) — Story V10-J2.
   */
  carriers: (mode?: string) => ['carriers', 'list', mode ?? 'live'] as const,
  /** One Versicherer's detail (GET /carriers/:uid) — P2 hardening 02. */
  carrier: (uid: string) => ['carriers', 'detail', uid] as const,
  /** A Versicherer's linked Schäden / Ausschreibungen / Kommunikation sub-lists. */
  carrierClaims: (uid: string) => ['carriers', uid, 'claims'] as const,
  carrierSubmissions: (uid: string) => ['carriers', uid, 'submissions'] as const,
  carrierKomm: (uid: string) => ['carriers', uid, 'kommunikation'] as const,

  /**
   * Rechnungen list (GET /invoices) — Story V10-J2.
   */
  invoices: (filters: Record<string, unknown> = {}) => ['invoices', 'list', filters] as const,

  /**
   * Invoice check-detail peek facts (GET /invoices/:id/check-detail) — Story UI-U4.
   */
  invoiceCheckDetail: (id: string) => ['invoices', 'check-detail', id] as const,

  /**
   * Invoice check history / Verlauf (GET /admin/invoice-check/:id/history) — Story UI-U4.
   */
  invoiceHistory: (id: string) => ['invoices', 'history', id] as const,

  /**
   * Policy list (GET /policies with filters) — Story V10-J2.
   */
  policies: (filters: Record<string, unknown> = {}) => ['policies', 'list', filters] as const,

  /**
   * Single policy detail incl. versions (GET /policies/:id + /versions) — Story V10-J2.
   */
  policy: (id: string) => ['policies', 'detail', id] as const,

  /**
   * Police-Ebene Ist-Attribute (GET /policies/:id/risk-attributes) — P2 Placement Slice 2.
   * `mode` is part of the key so a live/mock flip never serves the other from cache.
   */
  policyRiskAttributes: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['policies', 'risk-attributes', id, mode] as const,

  /**
   * Police-Ebene Soll-Deckung & Gap (GET /policies/:id/target-coverage) — P2 Placement Slice 2.
   */
  policyTargetCoverage: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['policies', 'target-coverage', id, mode] as const,

  /**
   * Police-Ebene Schadenverlauf / Schadenauskunft (GET /policies/:id/claims-history) — P2 Slice 6.
   * `mode` is part of the key so a live/mock flip never serves the other from cache.
   */
  policyClaimsHistory: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['policies', 'claims-history', id, mode] as const,

  /**
   * Kunde Ausschreibungen / Placements list (GET /placements?subject_type&subject_id) — P2 Slice 4.
   * `mode` is part of the key so a live/mock flip never serves the other from cache.
   */
  placements: (
    subjectType: string,
    subjectId: string,
    filters: Record<string, unknown> = {},
    mode: 'live' | 'mock' = 'mock',
  ) => ['placements', 'list', subjectType, subjectId, filters, mode] as const,

  /**
   * Firm-wide Ausschreibungen list (GET /placements with NO subject_type/subject_id) — the
   * sidebar «Ausschreibungen» screen. Shares the `['placements', 'list']` prefix so the
   * per-customer mutations' prefix-invalidation reaches it too.
   */
  placementsFirm: (filters: Record<string, unknown> = {}, mode: 'live' | 'mock' = 'mock') =>
    ['placements', 'list', 'firm', filters, mode] as const,

  /** A single Placement detail (GET /placements/:id) — P2 Slice 4. */
  placement: (id: string, mode: 'live' | 'mock' = 'mock') => ['placements', 'detail', id, mode] as const,

  /**
   * Ausschreibungs-Screen Tab 1 Risikoprofil snapshot (GET /placements/:id/risikoprofil) — P2 Slice 5.
   * `mode` is part of the key so a live/mock flip never serves the other from cache.
   */
  placementRisikoprofil: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['placements', 'risikoprofil', id, mode] as const,

  /** Ausschreibungs-Screen Tab 2 Soll-Deckungskatalog (GET /placements/:id/soll-deckung) — P2 Slice 5. */
  placementSollDeckung: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['placements', 'soll-deckung', id, mode] as const,

  /** Ausschreibungs-Screen Tab 3a Versicherer-Ranking (GET /placements/:id/versicherer/ranking) — P2 Slice 7. */
  placementVersichererRanking: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['placements', 'versicherer-ranking', id, mode] as const,

  /** Ausschreibungs-Screen Tab 3a Versicherer-Auswahl (GET /placements/:id/versicherer/auswahl) — P2 Slice 7. */
  placementVersichererAuswahl: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['placements', 'versicherer-auswahl', id, mode] as const,

  /** Ausschreibungs-Screen Tab 3b Submissions (GET /placements/:id/submissions) — P2 Slice 8. */
  placementSubmissions: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['placements', 'submissions', id, mode] as const,

  /** Ausschreibungs-Screen Tab 3c Rückfragen / Q&A (GET /placements/:id/qna) — P2 Slice 9. */
  placementQna: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['placements', 'qna', id, mode] as const,

  /** Ausschreibungs-Screen Tab 4a Offert-Posteingang (GET /placements/:id/offerten) — P2 Slice 10. */
  placementOfferten: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['placements', 'offerten', id, mode] as const,

  /** Ausschreibungs-Screen Tab 4b KI-Offertvergleich (GET /placements/:id/vergleich) — P2 Slice 11. */
  placementVergleich: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['placements', 'vergleich', id, mode] as const,

  /** The Ü7 Offer-Score weights (GET /offer-score/gewichte) — P2 Slice 11 (Regelwerk admin). */
  offerScoreGewichte: (mode: 'live' | 'mock' = 'mock') =>
    ['offer-score', 'gewichte', mode] as const,

  /** Ausschreibungs-Screen Tab 4c Kundenfreigabe + Entscheid (GET /placements/:id/kundenfreigabe) — P2 Slice 12. */
  placementKundenfreigabe: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['placements', 'kundenfreigabe', id, mode] as const,

  /** Ausschreibungs-Screen Tab 5a Antrag + Signatur + Einreichung (GET /placements/:id/antrag) — P2 Slice 13. */
  placementAntrag: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['placements', 'antrag', id, mode] as const,

  /** Ausschreibungs-Screen Tab 5b Underwriting (GET /placements/:id/underwriting) — P2 Slice 14. */
  placementUnderwriting: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['placements', 'underwriting', id, mode] as const,

  /** Ausschreibungs-Screen Tab 6 Historie (GET /placements/:id/historie) — P2 Slice 15. */
  placementHistorie: (id: string, quelle: 'aktivitaet' | 'alle', mode: 'live' | 'mock' = 'mock') =>
    ['placements', 'historie', id, quelle, mode] as const,

  /** Ausschreibungs-Screen Tab 6 Abschluss (GET /placements/:id/abschluss) — P2 Slice 15. */
  placementAbschluss: (id: string, mode: 'live' | 'mock' = 'mock') =>
    ['placements', 'abschluss', id, mode] as const,

  /**
   * Communication overview bucket (GET /communications?bucket=) — Story V10-J2.
   */
  communications: (bucket: string, filters: Record<string, unknown> = {}) =>
    ['communications', bucket, filters] as const,

  /**
   * Customer status + "(in Verarbeitung)" overlay (GET /customer-status/:type/:id) — #6.
   */
  customerStatusOverlay: (subjectType: string, subjectId: string) =>
    ['customer-status', 'overlay', subjectType, subjectId] as const,

  /**
   * Per-customer Vorgang lifecycle board (GET /vorgaenge/board?subjectType=) — #6.
   */
  vorgangBoard: (subjectType: string, subjectId: string) =>
    ['vorgaenge', 'board', subjectType, subjectId] as const,

  /**
   * Per-customer Mandatskacheln (GET /customers/:type/:id/mandate-badges) - P4-S1 (7.6).
   */
  mandateBadges: (subjectType: string, subjectId: string) =>
    ['mandate', 'badges', subjectType, subjectId] as const,

  /** The customer's live Mandatsauflösung (GET /customers/:type/:id/termination) - P6. */
  termination: (subjectType: string, subjectId: string) =>
    ['mandate', 'termination', subjectType, subjectId] as const,

  /** The Folgenanalyse of the dialog «Mandat auflösen» for an Enddatum (the preview, or of a live one). */
  /** P6-S3: the Dankesbrief preview of the Freigabe dialog. */
  terminationLetterPreview: (subjectType: string, subjectId: string, endDate: string, receivedOn: string, kind: string) =>
    ['mandate', 'termination', subjectType, subjectId, 'brief-vorschau', endDate, receivedOn, kind] as const,
  /** P6-S3: one termination by id (its Dankesbrief state). */
  terminationById: (id: string) => ['mandate', 'termination', 'id', id] as const,
  /** P6-S3: the terminations by Dankesbrief status (the board's read-only section). */
  terminationLetters: (statuses: string) => ['mandate', 'termination-letters', statuses] as const,
  terminationFolgenanalyse: (subjectType: string, subjectId: string, endDate: string | null) =>
    ['mandate', 'termination', subjectType, subjectId, 'folgenanalyse', endDate ?? ''] as const,

  /** The Kündigung the intake read from a mail, behind its task (GET /tasks/:taskId/termination-proposal). */
  terminationProposal: (taskId: string) => ['mandate', 'termination-proposal', taskId] as const,

  /** Administration › Mandatsauflösung (GET /admin/einstellungen/mandatsaufloesung). */
  adminMandateTermination: () => ['admin', 'mandatsaufloesung'] as const,

  /**
   * The customer's Portalstatus chip (GET /customers/:type/:id/portal-status) (10.5).
   */
  portalStatus: (subjectType: string, subjectId: string) =>
    ['portal', 'status', subjectType, subjectId] as const,

  /**
   * Brokermandat: the customer's Register-Befund flag (GET /customers/:type/:id/register-befund).
   */
  registerBefund: (subjectType: string, subjectId: string) =>
    ['mandate', 'register-befund', subjectType, subjectId] as const,


  /**
   * Relationship feed (GET /persons|firms/:id/relationships) — Story V10-J2.
   */
  relationships: (subjectType: string, id: string) =>
    ['relationships', subjectType, id] as const,

  /**
   * Beziehungsnetz ego graph (GET /beziehungsnetz/:subjectType/:id) — Story UI-U8.
   */
  beziehungsnetz: (subjectType: string, id: string) =>
    ['beziehungsnetz', subjectType, id] as const,

  /**
   * A firm's email domains (GET /firms/:id/domains) - D2 multi-domain.
   */
  firmDomains: (firmaId: string) => ['firms', firmaId, 'domains'] as const,
  /** Firmenname autocomplete — one register fetch per 3-char stem (GET /register/firm-names). */
  firmNameSuggestions: (stem: string) => ['register', 'firm-names', stem] as const,

  /**
   * Comm references (GET /communications/:commId/references) — Story UI-U8.
   */
  commReferences: (commId: string) => ['communications', commId, 'references'] as const,

  /**
   * Comm-assist KI summary/draft/confidence (GET /communications/:commId/assist)
   * — Story UI-U9. Endpoint owned by the backend-gaps wave; feature-detected.
   */
  commAssist: (commId: string) => ['communications', commId, 'assist'] as const,

  /**
   * Split-view detail for one Eingang communication (PART A3 — comm-keyed).
   * Resolved via GET /tasks?comm_id=… → GET /tasks/:id (the only body source).
   */
  commDetail: (commId: string) => ['communications', 'detail', commId] as const,

  /**
   * Tier-projected body of one communication (GET /communications/:commId/content).
   * Works for ANY comm (task-linked or not) — the body source for the reading pane (Fix C).
   */
  commContent: (commId: string) => ['communications', commId, 'content'] as const,

  // ── Freigabe / access-workflow (PART C) ──────────────────────────────────
  /** Access requests on ONE communication (GET /communications/:commId/access-requests). */
  accessRequestsForComm: (commId: string) =>
    ['communications', commId, 'access-requests'] as const,
  /** The decider inbox (GET /communications/access-requests?role=decider). */
  accessRequestsDecider: (filters: Record<string, unknown> = {}) =>
    ['communications', 'access-requests', 'decider', filters] as const,
  /** Standing Managed-Access grants (GET /communications/access-grants). */
  accessGrants: (filters: Record<string, unknown> = {}) =>
    ['communications', 'access-grants', filters] as const,

  /**
   * Global search (GET /search?q=) — Story V10-K.
   * The query string is the key input (tenant is implicit from the token).
   */
  search: (q: string) => ['search', q] as const,

  /**
   * DMS M12: the Policies-page "Semantisch" toggle (GET /search?scope=policy&q=).
   * Distinct key from `search(q)` — a different `scope` is a different request/response.
   */
  policySemanticSearch: (q: string) => ['search', 'policy-scope', q] as const,

  /**
   * ea/fix-search: the Ablage-page "Semantisch" toggle (GET /search?scope=document&q=).
   * Same reasoning as `policySemanticSearch` — a different `scope` is a different
   * request/response, so it gets its own cache key.
   */
  documentSemanticSearch: (q: string) => ['search', 'document-scope', q] as const,

  /**
   * DMS M12: the Policies-page "See history" per-row version chain
   * (GET /policies/:id/versions, filtered to superseded rows client-side).
   */
  policyVersions: (policyId: string) => ['policies', policyId, 'versions'] as const,

  /**
   * DMS M12: the Client → Dokumente tab (GET /documents?client_id=&state=active).
   */
  clientDocuments: (clientId: string) => ['documents', 'list', 'client', clientId] as const,

  /**
   * Sidebar nav-count probes (Story UI-U2 AC 3) — single key, one object result.
   */
  navCounts: () => ['nav', 'counts'] as const,

  /**
   * Own-tenant display data (GET /tenant/self) — Story UI-U2 AC 5.
   */
  tenantSelf: () => ['tenant', 'self'] as const,

  /**
   * Portfolio analytics aggregate (GET /statistics/portfolio) — Story UI-U7.
   */
  portfolioStatistics: () => ['statistics', 'portfolio'] as const,

  // ── Courtage (Story UI-U5) — GET /commission/statements[:id]|akonto|splits|
  //    expectations + the existing reconciliation Kennzahlen ──────────────────
  courtageStatements: (filters: Record<string, unknown> = {}) =>
    ['courtage', 'statements', filters] as const,
  courtageStatement: (id: string) => ['courtage', 'statement', id] as const,
  courtageAkonto: () => ['courtage', 'akonto'] as const,
  courtageSplits: () => ['courtage', 'splits'] as const,
  courtageKennzahlen: (period: string) => ['courtage', 'kennzahlen', period] as const,
  courtageExpectations: (period: string) => ['courtage', 'expectations', period] as const,

  // ── User settings + notifications (Parts C/D) ─────────────────────────────
  /** The caller's own profile (GET /me/profile). */
  meProfile: () => ['me', 'profile'] as const,
  /** The caller's in-app notifications + unread count (GET /notifications). */
  notifications: () => ['notifications', 'list'] as const,

  // ── Administration (Story UI-U6) — broker settings area ───────────────────
  /** The caller's effective permission keys + wildcard flag (GET /me/effective-permissions). */
  effectivePermissions: () => ['me', 'effective-permissions'] as const,
  /** The caller's effective denied visibility keys (GET /me/effective-visibility). */
  effectiveVisibility: () => ['me', 'effective-visibility'] as const,
  /** Tenant members (GET /admin/members). */
  adminMembers: () => ['admin', 'members'] as const,
  /** The roles a single user holds (GET /admin/users/:userId/roles). */
  adminUserRoles: (userId: string) => ['admin', 'user-roles', userId] as const,
  /** All roles in the tenant (GET /admin/roles). */
  adminRoles: () => ['admin', 'roles'] as const,
  /** The permission catalog + per-role selections (GET /admin/roles/catalog). */
  adminRolesCatalog: () => ['admin', 'roles', 'catalog'] as const,
  /** The visibility catalog (GET /admin/visibility/catalog). */
  adminVisibilityCatalog: () => ['admin', 'visibility', 'catalog'] as const,
  /** A role's visibility denies (GET /admin/roles/:roleId/visibility-denies). */
  adminRoleVisibilityDenies: (roleId: string) => ['admin', 'visibility', 'denies', roleId] as const,
  /** The field-permission catalog (GET /admin/fields/catalog — WS1 §5.2). */
  adminFieldCatalog: () => ['admin', 'fields', 'catalog'] as const,
  /** A role's field rules (GET /admin/roles/:roleId/field-permissions — WS1 §5.2). */
  adminRoleFieldPermissions: (roleId: string) => ['admin', 'fields', 'role', roleId] as const,
  /** The caller's effective field restrictions (GET /me/effective-field-permissions). */
  effectiveFieldPermissions: () => ['me', 'effective-field-permissions'] as const,
  /** Funktionsgruppen / Teams (GET /admin/funktionsgruppen). */
  adminFunktionsgruppen: () => ['admin', 'funktionsgruppen'] as const,
  /** One Funktionsgruppe incl. members (GET /admin/funktionsgruppen/:id). */
  adminFunktionsgruppe: (id: string) => ['admin', 'funktionsgruppen', id] as const,
  adminKundenteams: () => ['admin', 'kundenteams'] as const,
  adminKundenteam: (id: string) => ['admin', 'kundenteams', id] as const,
  /** Berechtigungskonzept V3.0 · Bereich 5 — the cross-customer control backlog (Rückstand). */
  adminControlBacklog: () => ['admin', 'kontrolle', 'rueckstand'] as const,
  // ── Berechtigungskonzept V4.0 · Bereich 5 Kontrollbereich Cockpit (Kapitel 8) ──
  /** The dashboard-panel aggregate (GET /admin/kontrolle/uebersicht). */
  kontrolleUebersicht: () => ['admin', 'kontrolle', 'uebersicht'] as const,
  /** The Kontrollliste for one stage (GET /admin/kontrolle/rueckstand?stufe=N). */
  kontrolleRueckstandStufe: (stufe: number) =>
    ['admin', 'kontrolle', 'rueckstand', 'stufe', stufe] as const,
  /** One backlog element's detail (GET /admin/kontrolle/rueckstand/:id). */
  kontrolleRueckstandDetail: (id: string) =>
    ['admin', 'kontrolle', 'rueckstand', 'detail', id] as const,
  /** The per-broker stage Fristen config (GET /admin/einstellungen/kontrolle-fristen). */
  kontrolleFristen: () => ['admin', 'kontrolle', 'fristen'] as const,
  /** Berechtigungskonzept V3.0 / Einfuehrungskonzept · P7 — the global reminder-frequency config. */
  adminProcessReminderConfig: () => ['admin', 'process-reminders'] as const,
  /** P4-S2 — the P-04 Mahnwesen parameter set + the P-04 Kommunikationsbausteine. */
  adminMahnwesenConfig: (processKey: string) => ['admin', 'mahnwesen', processKey] as const,
  adminTextTemplates: (processKey: string) => ['admin', 'text-templates', processKey] as const,

  /** The consolidated Kontrollen-Prüfbericht of a Vorgang (GET /vorgaenge/:id/pruefbericht). */
  pruefbericht: (vorgangId: string) => ['vorgaenge', 'pruefbericht', vorgangId] as const,
  /** A signature image of the Prüfbericht (fetched with the session). */
  pruefberichtSignatureImage: (vorgangId: string, personId: string, which: string) =>
    ['vorgaenge', 'pruefbericht', vorgangId, 'signature', personId, which] as const,
  /** Ausschlussliste / customer-suppression rows (GET /admin/suppression). */
  adminSuppression: () => ['admin', 'suppression'] as const,
  /** Berechtigungskonzept V3.0 · Bereich 1 (3.5) — the 3-tier e-mail blocklist. */
  emailBlocklist: () => ['email-blocklist'] as const,
  /** Tolerance config rows (GET /admin/tolerance-config). */
  adminToleranceConfig: () => ['admin', 'tolerance-config'] as const,
  /** P2 Placement Slice 3 — the Regelwerk Soll-Deckung catalog (GET /admin/regelwerk/soll-deckung). */
  adminRegelwerkSoll: (filters: Record<string, string | undefined> = {}) =>
    ['admin', 'regelwerk', 'soll-deckung', filters] as const,
  /** P2 Placement Slice 3 — the Regelwerk Attribut-Schema (GET /admin/regelwerk/attribut-schema). */
  adminRegelwerkSchema: (filters: Record<string, string | undefined> = {}) =>
    ['admin', 'regelwerk', 'attribut-schema', filters] as const,
  /** Channel config cards (GET /admin/channel-config). */
  adminChannelConfig: () => ['admin', 'channel-config'] as const,
  /** Channel matrix rows (GET /admin/channel-config/matrix). */
  adminChannelMatrix: () => ['admin', 'channel-config', 'matrix'] as const,
  /** Confidence-policy rows (GET /admin/confidence-policy). */
  adminConfidencePolicy: () => ['admin', 'confidence-policy'] as const,
  /** Group-routing rows (GET /admin/group-routing). */
  adminGroupRouting: () => ['admin', 'group-routing'] as const,
  /** Tenant config (GET /admin/tenant). */
  adminTenant: () => ['admin', 'tenant'] as const,
  /** Customer-portal config (GET /admin/customer-portal). */
  adminPortalConfig: () => ['admin', 'customer-portal'] as const,
  /** A customer's portal grants (GET /admin/customer-portal/grants/:type/:id). */
  adminPortalGrants: (subjectType: string, subjectId: string) =>
    ['admin', 'customer-portal', 'grants', subjectType, subjectId] as const,
  /** A customer's PRIMARY portal holder (GET /admin/customer-portal/access/:type/:id). */
  adminPortalAccess: (subjectType: string, subjectId: string) =>
    ['admin', 'customer-portal', 'access', subjectType, subjectId] as const,

  // ── Berechtigungskonzept V4.0 · Bereich 6 — Kundenportal-Berechtigungsgruppen ──
  /** The fixed IGB2B category taxonomy (GET /admin/customer-portal/igb2b-kategorien) — reference data. */
  igb2bKategorien: () => ['igb2b-kategorien'] as const,
  /** A customer's portal-visibility mode (GET /admin/customer-portal/visibility-mode/:type/:id). */
  portalVisibilityMode: (subjectType: string, subjectId: string) =>
    ['customers', subjectType, subjectId, 'portal-visibility-mode'] as const,
  /** A customer's Portal-Berechtigungsgruppen (GET /admin/customer-portal/portal-gruppen/:type/:id). */
  portalGruppen: (subjectType: string, subjectId: string) =>
    ['customers', subjectType, subjectId, 'portal-gruppen'] as const,
  /**
   * A customer's assignable portal-user pool — sourced from the portal grants admin endpoint
   * (GET /admin/customer-portal/grants/:type/:id) + the primary holder. Distinct cache key
   * from `adminPortalGrants` because the mapped/pool shape differs.
   */
  portalMemberPool: (subjectType: string, subjectId: string) =>
    ['customers', subjectType, subjectId, 'portal-member-pool'] as const,
  /** AVB library list/search (GET /avb). */
  adminAvb: (filters: Record<string, unknown> = {}) => ['admin', 'avb', filters] as const,
  /** Audit log (GET /audit) — filters are the query params. */
  adminAudit: (filters: Record<string, unknown> = {}) => ['admin', 'audit', filters] as const,
  /** API health (GET /health) — System-Status page. */
  adminHealth: () => ['admin', 'health'] as const,

  // ── Delegated mailbox (Story MC6) — the broker's OWN connected mailbox(es) ──
  /** Connected-mailbox list + feature flag (GET /mailbox/connections). Scoped by
   *  the auth token; no client input in the key. */
  mailboxConnections: () => ['mailbox', 'connections'] as const,

  // ── Berechtigungskonzept V3.0 · Bereich 1 — e-mail sync levels ─────────────
  /** The caller's per-mailbox sync levels + tenant cap (GET /email-sync/settings). */
  emailSyncSettings: () => ['email-sync', 'settings'] as const,
  /** The tenant cap on user sync levels (GET /admin/email-sync-cap). */
  adminEmailSyncCap: () => ['admin', 'email-sync-cap'] as const,

  // ── DMS (M11) — Ablage grid + inline PDF viewer ────────────────────────────
  /** Ablage grid list (GET /documents with filters). */
  documents: (filters: Record<string, unknown> = {}) => ['documents', 'list', filters] as const,
  /** Single document detail incl. connected_objects (GET /documents/:id). */
  documentDetail: (id: string) => ['documents', 'detail', id] as const,
  /** Inline-preview bytes (GET /documents/:id/content) - cached so reopening one refetches nothing. */
  documentContent: (id: string) => ['documents', 'content', id] as const,

  // ── P4-S7 Versicherer-Benachrichtigung ─────────────────────────────────────
  /** Every Versicherer-Benachrichtigung read: the prefix a send or a close invalidates. */
  versichererBenachrichtigung: () => ['versicherer-benachrichtigung'] as const,
  /** The dialog over a customer's declared insurances (GET /customers/:t/:id/versicherer-benachrichtigung). */
  versichererBenachrichtigungDialog: (subjectType: string, subjectId: string) =>
    ['versicherer-benachrichtigung', 'dialog', subjectType, subjectId] as const,
  /** The customer's Analysemandat for the panel (GET /customers/:t/:id/versicherer-benachrichtigung/mandate). */
  versichererBenachrichtigungMandate: (subjectType: string, subjectId: string) =>
    ['versicherer-benachrichtigung', 'mandate', subjectType, subjectId] as const,
  /** A mandate's notifications (GET /mandates/:id/versicherer-benachrichtigungen). */
  versichererBenachrichtigungen: (mandateId: string) =>
    ['versicherer-benachrichtigung', 'list', mandateId] as const,
  /** An Analysemandat's process state (GET /mandates/:id/process-state), under the same prefix. */
  mandateProcessState: (mandateId: string) => ['versicherer-benachrichtigung', 'process-state', mandateId] as const,
  /** The per-tenant Fristen (GET /admin/einstellungen/versicherer-fristen). */
  adminVersichererFristen: () => ['admin', 'versicherer-fristen'] as const,

  // ── the Brokermandat registration with the insurers ─────────────────────────
  /** A broker mandate's registrations (GET /mandates/:id/registrations). */
  registrationList: (mandateId: string) => ['registrations', 'list', mandateId] as const,
  /** A customer's registrations across its mandates (the subject view). */
  registrations: (subjectType: string, subjectId: string) => ['registrations', 'subject', subjectType, subjectId] as const,
  /** A customer's registration summary tile. */
  registrationTile: (subjectType: string, subjectId: string) => ['registrations', 'tile', subjectType, subjectId] as const,
  /** The registration behind a task of the reminder loop (GET /tasks/:id/registration). */
  taskRegistration: (taskId: string) => ['registrations', 'task', taskId] as const,
  /** The rows behind a task «Altbestand prüfen» (GET /tasks/:id/altbestand). */
  registrationAltbestand: (taskId: string) => ['registrations', 'altbestand', taskId] as const,
  /** The parsed insurer reply behind a review task (GET /tasks/:id/registration-proposal). */
  registrationProposal: (taskId: string) => ['registrations', 'proposal', taskId] as const,
  /** The candidate registrations of a Zuordnen task (GET /tasks/:id/registration-zuordnen). */
  registrationZuordnen: (taskId: string) => ['registrations', 'zuordnen', taskId] as const,
  /** The Beleg options of a registration, paged (GET /registrations/:regId/beleg-options). */
  registrationBelegOptions: (registrationId: string) => ['registrations', 'beleg-options', registrationId] as const,
  /** The customers registered at one insurer (the Versicherer's tab «Mandate»). */
  carrierRegistrations: (carrierUid: string) => ['registrations', 'carrier', carrierUid] as const,
  /** The Brokermandat Meldung settings (GET /admin/einstellungen/brokermandat-meldung). */
  adminBrokerRegistration: () => ['admin', 'brokermandat-meldung'] as const,
} as const;
